/**
 * Cost Mix — Matthew Blake's price-increase submissions tool.
 *
 * Left: cost build-up for the selected assortment, by FY, in the categories retailers ask for
 * in a CPI submission (Materials, Packaging, Labour, Overhead, Freight), with the baseline
 * (last accepted rise) marked and the cost movement since it — the case for the next submission.
 * Right/below: the editable submissions log (system of record; the latest Approved+effective row
 * per customer x assortment sets the baseline automatically).
 *
 * Cost data: commercial.JsonStore blob 'cost-mix' (built by scripts/build_cost_mix.py from COSTINGv2,
 * economic/actual basis — NOT standard cost). Submissions: /api/price-submissions (typed, audited).
 */
import { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { useApi } from '../hooks/useApi';
import { useBlob } from '../hooks/useBlob';
import DataGrid from '../components/grid/DataGrid';

const COMPONENTS = [
  { key: 'materials', label: 'Materials', color: '#2563eb' },
  { key: 'packaging', label: 'Packaging', color: '#0891b2' },
  { key: 'labour', label: 'Labour', color: '#7c3aed' },
  { key: 'overhead', label: 'Overhead', color: '#db2777' },
  { key: 'freight', label: 'Freight', color: '#ea580c' },
];
// D365 customer code → the COSTINGv2 ITEM_CUSTOMER[RetailerGroup] whose range scopes the basket.
const RETAILER_GROUP_BY_CUSTOMER = {
  CUS0044: 'Woolworths',
  CUS0027: 'Coles',
  CUS0004: 'Metcash',
  CUS0003: 'Costco',
};
const STATUS_COLORS = {
  Approved: '#16a34a', Submitted: '#2563eb', 'In review': '#d97706',
  Planned: '#7c3aed', Declined: '#dc2626', 'Not started': '#6b7280',
};
const fyToLabel = (fy) => `FY${String(fy).slice(2)}`;
const dateToFy = (iso) => { if (!iso) return null; const d = new Date(iso); return d.getUTCMonth() + 1 >= 7 ? d.getUTCFullYear() + 1 : d.getUTCFullYear(); };
const dateToPeriodLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso); const m = d.getUTCMonth() + 1;
  const fy = m >= 7 ? d.getUTCFullYear() + 1 : d.getUTCFullYear();
  const q = m >= 7 && m <= 9 ? 1 : m >= 10 ? 2 : m <= 3 ? 3 : 4;
  return `FY${String(fy).slice(2)} Q${q}`;
};
const round2 = (v) => Math.round((v || 0) * 100) / 100;

function CostTooltip({ active, payload, label, mode }) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((a, p) => a + (p.value || 0), 0);
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, padding: '10px 14px' }}>
      <div style={{ color: 'var(--text-primary)', fontWeight: 600, marginBottom: 6, fontSize: 12 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          <span style={{ color: 'var(--text-body)' }}>{p.name}:</span>
          <span style={{ color: 'var(--text-primary)' }}>{mode === 'pct' ? `${p.value.toFixed(1)}%` : `$${p.value.toFixed(2)}`}</span>
        </div>
      ))}
      {mode !== 'pct' && (
        <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--border-soft)', fontSize: 12, color: 'var(--text-primary)', fontWeight: 600 }}>
          Total: ${total.toFixed(2)}
        </div>
      )}
    </div>
  );
}

export default function CostMix() {
  const apiFetch = useApi();
  const queryClient = useQueryClient();

  const { data: costData, isLoading: costLoading } = useBlob('cost-mix');
  const { data: sub, isLoading: subLoading } = useQuery({
    queryKey: ['price-submissions'],
    queryFn: async () => {
      const r = await apiFetch('/price-submissions');
      if (!r.ok) throw new Error(`Failed to load submissions: HTTP ${r.status}`);
      return r.json();
    },
    refetchOnMount: 'always', retry: false,
  });

  const assortments = useMemo(() => sub?.assortments || [], [sub]);
  const submissions = useMemo(() => sub?.submissions || [], [sub]);
  const baseline = useMemo(() => sub?.baseline || [], [sub]);
  const statuses = useMemo(() => sub?.statuses || Object.keys(STATUS_COLORS), [sub]);

  const customers = useMemo(() => {
    const m = new Map();
    submissions.forEach((r) => { if (r.customerCode && !m.has(r.customerCode)) m.set(r.customerCode, r.customerName || r.customerCode); });
    return [...m.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [submissions]);
  const brands = useMemo(() => [...new Set(assortments.map((a) => a.brand).filter(Boolean))], [assortments]);

  const [customerCode, setCustomerCode] = useState('');
  const [brand, setBrand] = useState('');
  const [assortmentId, setAssortmentId] = useState('');
  const [perUnit, setPerUnit] = useState(false);
  const [asPct, setAsPct] = useState(false);
  const [gran, setGran] = useState('fy');      // 'fy' | 'q' — annual or quarterly
  const [view, setView] = useState('chart');   // 'chart' | 'table'
  const [showCommodities, setShowCommodities] = useState(false);
  const [flash, setFlash] = useState('');

  // Default the pickers once data arrives.
  const effCustomer = customerCode || customers[0]?.code || '';
  const effBrand = brand || brands[0] || '';
  const brandAssorts = useMemo(() => assortments.filter((a) => !effBrand || a.brand === effBrand), [assortments, effBrand]);
  const effAssortmentId = assortmentId || brandAssorts[0]?.id || '';
  const assortment = useMemo(() => assortments.find((a) => String(a.id) === String(effAssortmentId)), [assortments, effAssortmentId]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['price-submissions'] });
  const saveMut = useMutation({
    mutationFn: async ({ id, body }) => {
      const url = id ? `/price-submission/${id}` : '/price-submission';
      const r = await apiFetch(url, { method: id ? 'PUT' : 'POST', body: JSON.stringify(body) });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`Save failed: HTTP ${r.status} ${t.slice(0, 160)}`); }
      return r.json();
    },
    onSuccess: () => { invalidate(); setFlash('Saved'); setTimeout(() => setFlash(''), 1500); },
    onError: (e) => { setFlash(e.message); },
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => {
      const r = await apiFetch(`/price-submission/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`Delete failed: HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => { invalidate(); setFlash('Deleted'); setTimeout(() => setFlash(''), 1500); },
  });

  // ── Cost chart data for the selected assortment ─────────────────
  // Scope the basket to the retailer's actual range where we have one: the cost per
  // carton doesn't vary by customer, but the SKU *set* does (Coles ranges 6 of the 7
  // Bhuja Core SKUs), which moves the basket-weighted blend.
  const costCat = assortment?.costCategory;
  const catCostAll = costCat && costData?.categories ? costData.categories[costCat] : null;
  const retailerGroup = RETAILER_GROUP_BY_CUSTOMER[effCustomer] || null;
  const retailerScope = retailerGroup && catCostAll && !catCostAll.error
    ? catCostAll.retailers?.[retailerGroup] || null
    : null;
  const catCost = retailerScope || catCostAll;
  const hasCost = catCost && !catCost.error;
  const fys = costData?.fy || [2022, 2023, 2024, 2025, 2026];
  const periods = useMemo(() => costData?.periods || [], [costData]);
  const hasQuarterly = !!(catCost && catCost.quarterly);
  const quarterly = gran === 'q' && hasQuarterly;

  // x-axis columns: FY labels, or quarterly period labels when drilled in
  const cols = useMemo(
    () => (quarterly
      ? periods.map((p) => ({ key: p.key, label: p.label }))
      : fys.map((y) => ({ key: String(y), label: fyToLabel(y) }))),
    [quarterly, periods, fys],
  );

  const chartData = useMemo(() => {
    if (!hasCost) return [];
    const src = quarterly ? catCost.quarterly.components : catCost.components;
    return cols.map((col) => {
      const row = { label: col.label, key: col.key };
      let total = 0;
      COMPONENTS.forEach((c) => { const v = (src[c.key]?.[col.key] || 0) / (perUnit ? catCost.unitsPerCarton : 1); row[c.key] = v; total += v; });
      if (asPct && total > 0) COMPONENTS.forEach((c) => { row[c.key] = (row[c.key] / total) * 100; });
      row._total = total;
      return row;
    });
  }, [hasCost, catCost, cols, perUnit, asPct, quarterly]);

  // baseline for the selected customer x assortment
  const baseRow = useMemo(
    () => baseline.find((b) => b.customerCode === effCustomer && String(b.assortmentId) === String(effAssortmentId)),
    [baseline, effCustomer, effAssortmentId],
  );
  const baseFy = baseRow ? dateToFy(baseRow.effectiveDate) : null;
  // Anchor the baseline on the QUARTERLY grid: the first quarter-end on or after the rise took
  // effect. The FY grid cannot express a rise that lands mid-year — it snapped the anchor to
  // 30 June of that FY, which (a) collapsed the movement to +0.00% whenever the rise fell in the
  // CURRENT FY, because base and current became the same column, and (b) silently mis-anchored
  // every other mid-year rise (a Nov-2024 rise was measured from Jun-2025, eight months late,
  // understating the claim). Quarter-end ON OR AFTER the effective date is the conservative
  // choice: it measures from the cost level once the new price was already being paid, so we
  // never re-claim cost the retailer has settled. FYxQ4 reproduces the annual figure exactly
  // (verified across every category and retailer scope), so this is a strict generalisation.
  const basePeriod = useMemo(() => {
    if (!baseRow?.effectiveDate || !periods.length || !hasQuarterly) return null;
    return periods.find((p) => p.asof >= baseRow.effectiveDate) || null;
  }, [baseRow, periods, hasQuarterly]);
  const curPeriod = periods.length ? periods[periods.length - 1] : null;
  const baseColLabel = quarterly
    ? (basePeriod?.label || (baseRow?.effectiveDate ? dateToPeriodLabel(baseRow.effectiveDate) : null))
    : (baseFy ? fyToLabel(baseFy) : null);
  const gap = useMemo(() => {
    if (!hasCost) return null;
    const div = perUnit ? catCost.unitsPerCarton : 1;   // respect the $/unit vs $/carton toggle
    if (basePeriod && curPeriod && catCost.quarterly?.totalDelivered) {
      const qs = catCost.quarterly.totalDelivered;
      const base = (qs[basePeriod.key] || 0) / div;
      const cur = (qs[curPeriod.key] || 0) / div;
      if (!base) return null;
      return {
        deltaDollar: cur - base, deltaPct: cur / base - 1, base, cur,
        baseFy: basePeriod.fy, curFy: curPeriod.fy,
        baseLabel: basePeriod.label, curLabel: curPeriod.label,
        // Conversion and freight are FY actuals held flat across that FY's quarters, so a
        // baseline that is not a Q4 measures materials and packaging movement only — a floor.
        conversionFlatWithinFy: basePeriod.q !== 4,
      };
    }
    if (!baseFy) return null;
    const cur = (catCost.totalDelivered[String(fys[fys.length - 1])] || 0) / div;
    const base = (catCost.totalDelivered[String(baseFy)] || 0) / div;
    if (!base) return null;
    return {
      deltaDollar: cur - base, deltaPct: cur / base - 1, base, cur,
      baseFy, curFy: fys[fys.length - 1],
      baseLabel: fyToLabel(baseFy), curLabel: fyToLabel(fys[fys.length - 1]),
      conversionFlatWithinFy: false,
    };
  }, [hasCost, baseFy, basePeriod, curPeriod, catCost, fys, perUnit]);

  // ── Submissions grid ────────────────────────────────────────────
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const gridRows = useMemo(
    () => submissions.filter((r) => showAllCustomers || r.customerCode === effCustomer),
    [submissions, showAllCustomers, effCustomer],
  );
  // Which submission rows are the current baseline (latest approved rise per customer x assortment).
  const baselineKeys = useMemo(
    () => new Set(baseline.map((b) => `${b.customerCode}|${b.assortmentId}|${b.effectiveDate}`)),
    [baseline],
  );
  const isBaseline = useCallback(
    (row) => row && row.status === 'Approved' && baselineKeys.has(`${row.customerCode}|${row.assortmentId}|${row.effectiveDate}`),
    [baselineKeys],
  );
  // Lookups so Customer/Assortment can be edited inline (display name -> code/id on save).
  const customerNames = useMemo(() => customers.map((c) => c.name), [customers]);
  const assortmentNames = useMemo(() => assortments.map((a) => a.name), [assortments]);
  const custCodeByName = useMemo(() => Object.fromEntries(customers.map((c) => [c.name, c.code])), [customers]);
  const asstIdByName = useMemo(() => Object.fromEntries(assortments.map((a) => [a.name, a.id])), [assortments]);
  const columns = useMemo(() => [
    { headerName: 'Customer', field: 'customerName', width: 150, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: customerNames } },
    { headerName: 'Assortment', field: 'assortmentName', width: 170, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: assortmentNames } },
    { headerName: 'Submitted', field: 'submittedDate', width: 120, editable: true },
    { headerName: 'Effective', field: 'effectiveDate', width: 120, editable: true },
    {
      headerName: '% change', field: 'pctChange', width: 100, editable: true, cellDataType: 'number',
      valueFormatter: (p) => (p.value == null ? '' : `${(p.value * 100).toFixed(1)}%`),
      valueParser: (p) => { const n = parseFloat(String(p.newValue).replace('%', '')); return Number.isFinite(n) ? (n > 1 ? n / 100 : n) : null; },
    },
    {
      headerName: 'Status', field: 'status', width: 120, editable: true,
      cellEditor: 'agSelectCellEditor', cellEditorParams: { values: statuses },
      cellStyle: (p) => ({ color: STATUS_COLORS[p.value] || 'var(--text-body)', fontWeight: 600 }),
    },
    { headerName: 'Notes', field: 'notes', flex: 1, minWidth: 160, editable: true },
    {
      headerName: '', field: '_del', width: 76, editable: false, sortable: false, filter: false,
      cellRenderer: (p) => (
        <button
          onClick={() => { if (window.confirm('Delete this submission?')) deleteMut.mutate(p.data.id); }}
          style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >Delete</button>
      ),
    },
  ], [statuses, deleteMut, customerNames, assortmentNames]);

  const onCellValueChanged = useCallback((e) => {
    const r = e.data;
    // Customer/Assortment edits change the display name; resolve back to code/id for the write.
    const customerCode = custCodeByName[r.customerName] ?? r.customerCode;
    const assortmentId = asstIdByName[r.assortmentName] ?? r.assortmentId;
    saveMut.mutate({
      id: r.id,
      body: {
        customerCode, assortmentId,
        submittedDate: r.submittedDate, effectiveDate: r.effectiveDate,
        pctChange: r.pctChange, status: r.status, notes: r.notes,
      },
    }, { onError: () => invalidate() /* revert to server truth */ });
  }, [saveMut, custCodeByName, asstIdByName]);

  const addSubmission = () => {
    if (!effCustomer || !effAssortmentId) { setFlash('Pick a customer and assortment first'); return; }
    saveMut.mutate({ id: null, body: { customerCode: effCustomer, assortmentId: Number(effAssortmentId), status: 'Planned', notes: 'New submission' } });
  };

  const exportCost = async () => {
    if (!hasCost) return;
    const XLSX = await import('xlsx');
    const unit = asPct ? '% of total' : (perUnit ? '$/unit' : '$/carton');
    const scope = retailerScope ? `${retailerGroup} range` : 'all retailers';
    const header = [`${costCat} (${scope}, ${catCost.basketSkuCount} SKUs) — ${unit}`, ...chartData.map((d) => d.label)];
    const body = COMPONENTS.map((comp) => [comp.label, ...chartData.map((d) => round2(d[comp.key]))]);
    if (!asPct) body.push(['Total', ...chartData.map((d) => round2(d._total))]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cost Mix');
    XLSX.writeFile(wb, `cost-mix-${costCat}-${quarterly ? 'quarterly' : 'FY'}-${asPct ? 'pct' : 'dollar'}.xlsx`);
    setFlash('Exported');
    setTimeout(() => setFlash(''), 1500);
  };

  if (costLoading || subLoading) {
    return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading cost mix…</div>;
  }

  // Prefer the basket-scoped warnings on the payload we're actually showing, so the banner
  // describes the same SKU set as the numbers (the retailer view is a subset of the range).
  // Falls back to the flat category-level list for older cost-mix.json builds.
  const warnings = catCost?.uomWarnings
    || (costData?.warnings || []).filter((w) => w.category === costCat);

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1280, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Cost Mix</h1>
          <div style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
            What each assortment costs to make, and how far it has moved since the last accepted price rise — the case for the next submission.
          </div>
        </div>
        {flash && <div style={{ fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-elevated)', padding: '4px 10px', borderRadius: 6 }}>{flash}</div>}
      </div>

      {/* Pickers + toggles */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', margin: '16px 0' }}>
        <Select label="Customer" value={effCustomer} onChange={setCustomerCode} options={customers.map((c) => ({ value: c.code, label: c.name }))} />
        <Select label="Brand" value={effBrand} onChange={(v) => { setBrand(v); setAssortmentId(''); }} options={brands.map((b) => ({ value: b, label: b }))} />
        <Select label="Assortment" value={String(effAssortmentId)} onChange={setAssortmentId} options={brandAssorts.map((a) => ({ value: String(a.id), label: a.name }))} />
        <div style={{ flex: 1 }} />
        <Toggle label="$/carton" alt="$/unit" on={perUnit} onClick={() => setPerUnit((v) => !v)} />
        <Toggle label="$ value" alt="% of total" on={asPct} onClick={() => setAsPct((v) => !v)} />
      </div>

      {/* Cost build-up */}
      <div style={{ background: 'var(--bg-surface, var(--bg-elevated))', border: '1px solid var(--border-soft)', borderRadius: 12, padding: 16, marginBottom: 20 }}>
        {!assortment?.costCategory ? (
          <Empty>Cost mapping pending for <b>{assortment?.name || 'this assortment'}</b> — no COSTINGv2 category is linked yet.</Empty>
        ) : !hasCost ? (
          <Empty>No cost basket for <b>{costCat}</b> — no Active SKUs in this assortment are currently shipping.</Empty>
        ) : (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
                {assortment.name}{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  · {costCat} · {catCost.basketSkuCount}-SKU basket · {catCost.unitsPerCarton.toFixed(1)} units/ctn
                </span>{' '}
                <span
                  title={retailerScope
                    ? `Basket scoped to the ${retailerGroup} range (SKUs invoiced to ${retailerGroup})`
                    : 'Basket covers every Active shipping SKU in this category — no retailer range on file for this customer'}
                  style={{
                    fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 10,
                    border: '1px solid var(--border-soft)', color: 'var(--text-muted)', whiteSpace: 'nowrap',
                  }}
                >
                  {retailerScope ? `${retailerGroup} range` : 'All retailers'}
                </span>
              </div>
              {gap ? (
                <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                  Cost since last rise (<b>{gap.baseLabel}</b> → {gap.curLabel}):{' '}
                  <b style={{ color: gap.deltaPct >= 0 ? '#dc2626' : '#16a34a' }}>{gap.deltaPct >= 0 ? '+' : ''}{(gap.deltaPct * 100).toFixed(1)}%</b>{' '}
                  ({gap.deltaDollar >= 0 ? '+' : ''}${gap.deltaDollar.toFixed(perUnit ? 3 : 2)}/{perUnit ? 'unit' : 'ctn'})
                  {gap.conversionFlatWithinFy && (
                    <div style={{ fontSize: 11, color: '#d97706', marginTop: 2 }}>
                      ⚠ The rise landed mid-year. Labour, overhead and freight are financial-year
                      actuals held flat across that year&apos;s quarters, so this measures materials
                      and packaging movement only — treat it as a floor, not the full case.
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No approved baseline yet for {customers.find((c) => c.code === effCustomer)?.name || 'this customer'}.</div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
              {hasQuarterly && <Toggle label="Annual (FY)" alt="Quarterly" on={gran === 'q'} onClick={() => setGran((g) => (g === 'q' ? 'fy' : 'q'))} />}
              <Toggle label="Chart" alt="Table" on={view === 'table'} onClick={() => setView((v) => (v === 'table' ? 'chart' : 'table'))} />
              <div style={{ flex: 1 }} />
              <button onClick={exportCost} style={{ ...linkBtn, fontWeight: 600, color: '#2563eb' }}>⭳ Export to Excel</button>
            </div>
            {view === 'chart' ? (
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={chartData} margin={{ left: 8, right: 16, top: 8, bottom: quarterly ? 8 : 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: 'var(--text-body)', fontSize: quarterly ? 10 : 12 }} axisLine={false} tickLine={false}
                    angle={quarterly ? -45 : 0} textAnchor={quarterly ? 'end' : 'middle'} height={quarterly ? 54 : 30} interval={0} />
                  <YAxis tickFormatter={(v) => (asPct ? `${v}%` : `$${v}`)} tick={{ fill: 'var(--text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
                  <Tooltip content={<CostTooltip mode={asPct ? 'pct' : 'dollar'} />} cursor={{ fill: '#8881' }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-body)' }} />
                  {baseColLabel && <ReferenceLine x={baseColLabel} stroke="#64748b" strokeDasharray="4 2" label={{ value: 'baseline', fill: '#64748b', fontSize: 10, position: 'top' }} />}
                  {COMPONENTS.map((c, i) => (
                    <Bar key={c.key} dataKey={c.key} name={c.label} stackId="a" fill={c.color} radius={i === COMPONENTS.length - 1 ? [3, 3, 0, 0] : undefined} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <CostMatrix chartData={chartData} asPct={asPct} baseColLabel={baseColLabel} />
            )}

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
              <button onClick={() => setShowCommodities((v) => !v)} style={{ ...linkBtn, fontWeight: 600, color: '#2563eb' }}>
                {showCommodities ? '▾ Hide' : '▸ Show'} Materials breakdown — commodity input prices ($/kg)
              </button>
              {warnings.length > 0 && (
                <span style={{ fontSize: 12, color: '#d97706' }}>
                  ⚠ {warnings.length} ingredient(s) in this basket have a likely master-data UOM error —
                  the cost above still includes them. Fix the D365 master data before submitting.
                </span>
              )}
              {!warnings.length && catCost.uomUnverifiedCount > 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {catCost.uomUnverifiedCount} input(s) could not be independently verified (no unit cost
                  and no price history spanning the FY24 costing change).
                </span>
              )}
            </div>
            {showCommodities && (
              <CommodityTable commodities={catCost.commodities} fys={fys} warnings={warnings} />
            )}
          </>
        )}
      </div>

      {/* Submissions log */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Submissions log</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 4, alignItems: 'center' }}>
            <input type="checkbox" checked={showAllCustomers} onChange={(e) => setShowAllCustomers(e.target.checked)} /> all customers
          </label>
          <button onClick={addSubmission} style={{ ...linkBtn, fontWeight: 600, color: '#2563eb' }}>+ Add submission</button>
        </div>
      </div>
      <DataGrid rowData={gridRows} columnDefs={columns} onCellValueChanged={onCellValueChanged}
        getRowId={(p) => String(p.data.id)}
        getRowStyle={(p) => (isBaseline(p.data) ? { background: 'rgba(22,163,74,0.12)' } : undefined)}
        height={360} />
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
        <span style={{ color: '#16a34a', fontWeight: 700 }}>▬</span> Green rows are the current <b>baseline</b> — the latest Approved rise (effective on/before today) per customer × assortment. That's the point the chart measures cost movement from.
      </div>

      {/* Assumptions */}
      <details style={{ marginTop: 16, fontSize: 12, color: 'var(--text-muted)' }}>
        <summary style={{ cursor: 'pointer' }}>Data &amp; assumptions</summary>
        <ul style={{ margin: '8px 0 0 18px', lineHeight: 1.6 }}>
          <li><b>Economic (actual) cost basis, not standard cost.</b> Materials/Packaging = current recipe × input price as-of each FY; Labour/Overhead = actual GL cash cost per carton; Freight = company blended rate allocated by carton weight.</li>
          <li>Standard cost is deliberately excluded from the case — the ~+36% it shows is the Oct-2023 overhead absorption-method change, not market cost.</li>
          <li>FY = Jul–Jun, as-of 30 Jun (FY26 is partial).</li>
          <li><b>Basket = today's Active range</b> for the assortment (SKUs currently sold and shipping), weighted by recent cartons and priced back through time on the current recipe × historic input prices. So the carton spec matches what we sell now, and the trend is a pure price effect (same SKU set at every point). An assortment with no Active shipping SKUs shows no cost.</li>
          <li><b>Quarterly view:</b> Materials/Packaging are priced as-of each quarter-end (real commodity movement); Labour/Overhead/Freight are the FY GL actual held across that FY's quarters. FY = the Q4 value.</li>
          <li>Cost is at <b>assortment (category) grain</b> — it does not vary by customer for branded lines; the customer picker only drives the baseline and submissions.</li>
          <li>Manual per submission: country of origin; the proposed % price move.</li>
          {costData?.generatedAt && <li>Cost data generated {costData.generatedAt}.</li>}
        </ul>
      </details>
    </div>
  );
}

function CommodityTable({ commodities, fys, warnings }) {
  const flagged = new Set(warnings.map((w) => w.component));
  return (
    <div style={{ overflowX: 'auto', marginTop: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
            <th style={{ textAlign: 'left', padding: '4px 8px' }}>Commodity ($/kg)</th>
            {fys.map((y) => <th key={y} style={{ padding: '4px 8px' }}>{fyToLabel(y)}</th>)}
            <th style={{ padding: '4px 8px' }}>Δ</th>
          </tr>
        </thead>
        <tbody>
          {(commodities || []).map((c, i) => (
            <tr key={i} style={{ borderTop: '1px solid var(--border-soft)', color: 'var(--text-body)', textAlign: 'right' }}>
              <td style={{ textAlign: 'left', padding: '4px 8px', color: flagged.has(c.name) ? '#d97706' : 'var(--text-body)' }}>
                {flagged.has(c.name) && '⚠ '}{c.name}
              </td>
              {fys.map((y) => <td key={y} style={{ padding: '4px 8px' }}>{c.series[String(y)] != null ? c.series[String(y)].toFixed(2) : '—'}</td>)}
              <td style={{ padding: '4px 8px', color: c.pctChange > 0 ? '#dc2626' : '#16a34a' }}>{c.pctChange == null ? '—' : `${(c.pctChange * 100).toFixed(0)}%`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostMatrix({ chartData, asPct, baseColLabel }) {
  const fmt = (v) => (asPct ? `${(v || 0).toFixed(1)}%` : `$${(v || 0).toFixed(2)}`);
  const stickyHead = { textAlign: 'left', padding: '5px 10px', position: 'sticky', left: 0, background: 'var(--bg-elevated)' };
  const stickyCell = { textAlign: 'left', padding: '5px 10px', position: 'sticky', left: 0, background: 'var(--bg-surface, var(--bg-elevated))' };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%', whiteSpace: 'nowrap' }}>
        <thead>
          <tr style={{ color: 'var(--text-muted)', textAlign: 'right' }}>
            <th style={stickyHead}>Component</th>
            {chartData.map((d) => (
              <th key={d.key} style={{ padding: '5px 10px', color: d.label === baseColLabel ? '#16a34a' : 'var(--text-muted)', fontWeight: d.label === baseColLabel ? 700 : 400 }}>
                {d.label}{d.label === baseColLabel ? ' ◄' : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COMPONENTS.map((comp) => (
            <tr key={comp.key} style={{ borderTop: '1px solid var(--border-soft)', textAlign: 'right', color: 'var(--text-body)' }}>
              <td style={stickyCell}>
                <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: comp.color, marginRight: 6 }} />{comp.label}
              </td>
              {chartData.map((d) => <td key={d.key} style={{ padding: '5px 10px' }}>{fmt(d[comp.key])}</td>)}
            </tr>
          ))}
          {!asPct && (
            <tr style={{ borderTop: '2px solid var(--border-soft)', textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>
              <td style={{ ...stickyCell, color: 'var(--text-primary)' }}>Total delivered</td>
              {chartData.map((d) => <td key={d.key} style={{ padding: '5px 10px' }}>{fmt(d._total)}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: 'var(--text-body)', cursor: 'pointer', fontSize: 12, padding: 0 };

function Select({ label, value, onChange, options }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-soft)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 13, minWidth: 130 }}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Toggle({ label, alt, on, onClick }) {
  return (
    <button onClick={onClick} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-soft)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
      {on ? alt : label}
    </button>
  );
}

function Empty({ children }) {
  return <div style={{ padding: '28px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>{children}</div>;
}
